import {
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { api } from "common";
import { firebase } from "common/src/config/configureFirebase";
import { equalTo, off, onValue, orderByChild, query } from "firebase/database";
import MaterialTable from "material-table";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { colors } from "../components/Theme/WebTheme";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    color: colors.MAIN_COLOR,
    fontWeight: "bold",
  },
  subtitle: {
    color: colors.SECONDARY,
    marginTop: theme.spacing(1),
  },
  statsCard: {
    backgroundColor: colors.WHITE,
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    marginBottom: theme.spacing(3),
  },
  statsGrid: {
    padding: theme.spacing(2),
  },
  statItem: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  statNumber: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: colors.MAIN_COLOR,
  },
  statLabel: {
    color: colors.SECONDARY,
    marginTop: theme.spacing(1),
  },
  tableCard: {
    backgroundColor: colors.WHITE,
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  actionButton: {
    margin: theme.spacing(0.5),
    borderRadius: 20,
    textTransform: "none",
  },
  settingsButton: {
    position: "absolute",
    top: theme.spacing(12),
    right: theme.spacing(2),
  },
  addressTooltip: {
    maxWidth: 300,
  },
  driverModal: {
    minWidth: 600,
  },
  driverItem: {
    padding: theme.spacing(2),
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      borderColor: colors.MAIN_COLOR,
      backgroundColor: "#f8f9fa",
    },
  },
  selectedDriver: {
    borderColor: colors.MAIN_COLOR,
    backgroundColor: "#e3f2fd",
  },
  driverInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  driverDetails: {
    flex: 1,
  },
  driverDistance: {
    color: colors.MAIN_COLOR,
    fontWeight: "bold",
  },
  configSection: {
    marginBottom: theme.spacing(3),
  },
}));

const AdminQueue = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const dispatch = useDispatch();
  
  // Redux state
  const adminQueueState = useSelector((state) => state.adminQueueData);
  const settings = useSelector((state) => state.settingsdata.settings);
  
  // Local state
  const [adminQueueData, setAdminQueueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Cancel trip modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [tripToCancel, setTripToCancel] = useState(null);
  
  // Admin configuration state
  const [adminConfig, setAdminConfig] = useState({
    automaticNotification: false,
    automaticAssignment: false,
    automaticSearch: false,
    notificationRadius: 10,
    autoAssignmentTimeout: 30,
    notificationInterval: 30,
    notificationTitle: "New Order Available",
    notificationMessage: "A new order is available in your area. Tap to view details.",
  });

  // Update local state when Redux state changes
  useEffect(() => {
    if (adminQueueState && adminQueueState.adminQueue) {
      setAdminQueueData(adminQueueState.adminQueue);
      console.log("✅ Admin queue data updated from Redux:", adminQueueState.adminQueue);
    }
  }, [adminQueueState]);

  useEffect(() => {
    loadAdminQueueData();
    loadAdminConfiguration();
  }, []);

  // Firebase real-time listener for admin queue data
  useEffect(() => {
    console.log("🔥 Setting up Firebase real-time listener for admin queue...");
    
    const { bookingRef } = firebase;
    
    if (!bookingRef) {
      console.error("❌ bookingRef not found in Firebase config");
      return;
    }

    // Query for admin queue bookings
    const adminQueueQuery = query(
      bookingRef,
      orderByChild('status'),
      equalTo('ADMIN_QUEUE')
    );

    // Set up real-time listener
    const unsubscribe = onValue(adminQueueQuery, (snapshot) => {
      console.log("🔥 Firebase real-time data received for admin queue");
      
      if (snapshot.exists()) {
        const adminQueueData = snapshot.val();
        const adminQueueBookings = Object.keys(adminQueueData).map(key => ({
          id: key,
          ...adminQueueData[key]
        }));
        
        console.log("✅ Admin queue data updated from Firebase:", adminQueueBookings.length, "bookings");
        setAdminQueueData(adminQueueBookings);
        
        // Also update Redux store if needed
        if (adminQueueState) {
          dispatch({
            type: 'ADMIN_QUEUE_ACTIONS/FETCH_ADMIN_QUEUE_SUCCESS',
            payload: adminQueueBookings
          });
        }
      } else {
        console.log("📭 No admin queue bookings found in Firebase");
        setAdminQueueData([]);
      }
    }, (error) => {
      console.error("❌ Firebase real-time listener error:", error);
      // Don't fallback to API call to avoid conflicts with real-time updates
      console.log("⚠️ Using existing data instead of API fallback");
    });

    // Cleanup function
    return () => {
      console.log("🧹 Cleaning up Firebase real-time listener for admin queue");
      off(adminQueueQuery);
    };
  }, [firebase, dispatch, adminQueueState]); // Add proper dependencies

  const loadAdminQueueData = async () => {
    setLoading(true);
    try {
      console.log("🔄 Loading admin queue data...");
      // Dispatch action to fetch data
      await dispatch(api.fetchAdminQueue());
      console.log("📊 Admin queue action dispatched");
    } catch (error) {
              console.error("❌ Error loading admin queue:", error);
      showSnackbar(t('admin_queue_error_failed_to_load_data'), "error");
      setAdminQueueData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminConfiguration = async () => {
    try {
      // Real API call
      const config = await api.getAdminConfiguration();
      if (config) {
        // Merge with default values to ensure all fields exist
        setAdminConfig({
          automaticNotification: config.automaticNotification || false,
          automaticAssignment: config.automaticAssignment || false,
          automaticSearch: config.automaticSearch || false,
          notificationRadius: config.notificationRadius || 10,
          autoAssignmentTimeout: config.notificationInterval || 30,
          notificationInterval: config.notificationInterval || 30,
          notificationTitle: config.notificationTitle || "New Order Available",
          notificationMessage: config.notificationMessage || "A new order is available in your area. Tap to view details.",
        });
        console.log("✅ Admin configuration loaded:", config);
      }
    } catch (error) {
      console.error("Error loading admin config:", error);
      // Set default values if API fails
      setAdminConfig({
        automaticNotification: false,
        automaticAssignment: false,
        automaticSearch: false,
        notificationRadius: 10,
        autoAssignmentTimeout: 30,
        notificationInterval: 30,
        notificationTitle: "New Order Available",
        notificationMessage: "A new order is available in your area. Tap to view details.",
      });
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleAssignDriver = async (booking) => {
    setSelectedBooking(booking);
    setSelectedDriver(null);
    
    try {
      // Real API call - use driverRadius from settings instead of adminConfig
      const radius = settings?.driverRadius || 10; // Default to 10 if not set
      console.log("🔍 Searching for drivers with radius:", radius, "km");
      
      const drivers = await api.getAvailableDrivers(booking.pickup, radius);
      if (drivers) {
        setAvailableDrivers(drivers);
        setDriverModalOpen(true);
      } else {
        showSnackbar(t('admin_queue_warning_no_drivers_found'), "warning");
      }
    } catch (error) {
              console.error("Error loading drivers:", error);
              showSnackbar(t('admin_queue_error_failed_to_load_drivers'), "error");
    }
  };

  const handleCancelTrip = async (booking) => {
    setTripToCancel(booking);
    setCancelModalOpen(true);
  };

  const confirmCancelTrip = async () => {
    if (!tripToCancel) return;

    try {
      // Real API call
      const result = await dispatch(api.cancelTripFromAdminQueue(tripToCancel.id, "Cancelled by admin"));
      if (result.success) {
        // Don't manually update local state - Firebase listener will handle it
        showSnackbar(t('admin_queue_success_trip_cancelled', { tripId: tripToCancel.id }), "success");
      } else {
        showSnackbar(t('admin_queue_error_failed_to_cancel_trip'), "error");
      }
    } catch (error) {
      console.error("Error cancelling trip:", error);
      showSnackbar(t('admin_queue_error_failed_to_cancel_trip'), "error");
    } finally {
      setCancelModalOpen(false);
      setTripToCancel(null);
    }
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setTripToCancel(null);
  };

  const handleDriverSelect = (driver) => {
    setSelectedDriver(driver);
  };

  const handleDriverAssignment = async () => {
    if (!selectedDriver || !selectedBooking) return;

    setAssigning(true);
    try {
      // Real API call
      const result = await dispatch(api.assignDriverToBooking(selectedBooking.id, selectedDriver.id, selectedDriver));
      
      if (result.success) {
        // Don't manually update local state - Firebase listener will handle it
        showSnackbar(t('admin_queue_success_driver_assigned', { driverName: selectedDriver.name, tripId: selectedBooking.id }), "success");
        
        setDriverModalOpen(false);
        setSelectedBooking(null);
        setSelectedDriver(null);
      } else {
        showSnackbar(t('admin_queue_error_failed_to_assign_driver'), "error");
      }
    } catch (error) {
      console.error("Error assigning driver", error);
      showSnackbar(t('admin_queue_error_failed_to_assign_driver'), "error");
    } finally {
      setAssigning(false);
    }
  };

  const handleConfigUpdate = async () => {
    try {
      console.log("🔄 Updating admin configuration:", adminConfig);
      
      // Validate required fields
      if (!adminConfig.notificationTitle || !adminConfig.notificationMessage) {
        showSnackbar(t('admin_queue_error_notification_fields_required'), "error");
        return;
      }
      
      // Real API call
      const result = await dispatch(api.updateAdminConfiguration(adminConfig));
      
      if (result.success) {
        showSnackbar(t('admin_queue_success_configuration_updated'), "success");
        console.log("✅ Admin configuration updated successfully");
        handleSettingsClose();
      } else {
        showSnackbar(t('admin_queue_error_failed_to_update_config'), "error");
      }
    } catch (error) {
      console.error("Error updating config:", error);
      showSnackbar(t('admin_queue_error_failed_to_update_config'), "error");
    }
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";
    return address.length > 8 ? `${address.substring(0, 8)}...` : address;
  };

  const columns = [
    {
      title: t('admin_queue_table_orderId'),
      field: "id",
      width: 120,
      render: (rowData) => (
        <Typography variant="body2" style={{ fontWeight: "bold", color: colors.MAIN_COLOR }}>
          {rowData.id}
        </Typography>
      ),
    },
    {
      title: t('admin_queue_table_name'),
      field: "customer_name",
      width: 150,
      render: (rowData) => (
        <Tooltip title={rowData.customer_name} classes={{ tooltip: classes.addressTooltip }}>
          <Typography variant="body2" style={{ fontWeight: "500", textTransform: 'capitalize' }}>
            {rowData.customer_name.length > 15 ? `${rowData.customer_name.substring(0, 15)}...` : rowData.customer_name}
          </Typography>
        </Tooltip>
      ),
    },
    {
      title: t('admin_queue_table_bookingDate'),
      field: "bookingDate",
      width: 130,
      render: (rowData) => (
        <Typography variant="body2">
          {moment(rowData.bookingDate).format("DD/MM/YYYY")}
        </Typography>
      ),
    },
    {
      title: t('admin_queue_table_bookingTime'),
      field: "bookingDate",
      width: 120,
      render: (rowData) => (
        <Typography variant="body2">
          {moment(rowData.bookingDate).format("HH:mm")}
        </Typography>
      ),
    },
    {
      title: t('admin_queue_table_vehicleType'),
      field: "carType",
      width: 120,
      render: (rowData) => (
        <Tooltip title={rowData.carType} classes={{ tooltip: classes.addressTooltip }}>
          {rowData.carType.length > 12 ? `${rowData.carType.substring(0, 12)}...` : rowData.carType}
        </Tooltip>
      ),
    },
    {
      title: t('admin_queue_table_distance'),
      field: "distance",
      width: 100,
      render: (rowData) => (
        <Typography variant="body2">
          {rowData.distance} {t('admin_queue_table_km')}
        </Typography>
      ),
    },
    {
      title: t('admin_queue_table_origin'),
      field: "pickup.add",
      width: 200,
      render: (rowData) => (
        <Tooltip title={rowData.pickup?.add || t('adminQueue.table.na')} classes={{ tooltip: classes.addressTooltip }}>
          <Typography variant="body2" style={{ cursor: "pointer" }}>
            {rowData.pickup?.add && rowData.pickup.add.length > 25 ? `${rowData.pickup.add.substring(0, 25)}...` : (rowData.pickup?.add || t('adminQueue.table.na'))}
          </Typography>
        </Tooltip>
      ),
    },
    {
      title: t('admin_queue_table_destination'),
      field: "drop.add",
      width: 200,
      render: (rowData) => (
        <Tooltip title={rowData.drop?.add || t('adminQueue.table.na')} classes={{ tooltip: classes.addressTooltip }}>
          <Typography variant="body2" style={{ cursor: "pointer" }}>
            {rowData.drop?.add && rowData.drop.add.length > 25 ? `${rowData.drop.add.substring(0, 25)}...` : (rowData.drop.add || t('adminQueue.table.na'))}
          </Typography>
        </Tooltip>
      ),
    },
    {
      title: t('admin_queue_table_action'),
      field: "actions",
      width: 150,
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <Select
          value=""
          displayEmpty
          size="small"
          variant="outlined"
          onChange={(event) => {
            const action = event.target.value;
            if (action === "assign") {
              handleAssignDriver(rowData);
            } else if (action === "cancel") {
              handleCancelTrip(rowData);
            }
          }}
          sx={{
            minWidth: 120,
            backgroundColor: colors.MAIN_COLOR,
            fontSize: 14,
            color: colors.WHITE,
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: colors.WHITE,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.MAIN_COLOR,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.MAIN_COLOR,
            },
            "& .MuiSvgIcon-root": {
              color: colors.WHITE,
            },
          }}
        >
          <MenuItem value="" disabled style={{ fontSize: 14 }}>
            {t('admin_queue_table_selectAction')}
          </MenuItem>
          <MenuItem value="assign" style={{ fontSize: 14 }}>
            {t('admin_queue_table_assignDriver')}
          </MenuItem>
          <MenuItem value="cancel" style={{ fontSize: 14 }}>
            {t('admin_queue_table_cancelTrip')}
          </MenuItem>
        </Select>
      ),
    },
  ];

  if (loading) {
    return <CircularLoading />;
  }

  return (
    <div className={classes.root}>
      {/* Header */}
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            {t('admin_queue_title')}
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            {t('admin_queue_subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', whiteSpace: 'nowrap', width: '100%' }}>
            <IconButton
              className={classes.settingsButton}
              onClick={handleSettingsOpen}
              color="primary"
              title={t('admin_queue_settings_button')}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </div>

      {/* Admin Queue Table */}
      <MaterialTable
        title={t('admin_queue_table_title')}
        columns={columns}
        data={adminQueueData}
        style={{ 
          borderRadius: 12
         }}
        options={{
          pageSize: 5,
          pageSizeOptions: [5, 10, 20],
          sorting: true,
          search: true,
          selection: false,
          showSelectAllCheckbox: false,
          showTextRowsSelected: false,
          toolbar: true,
          toolbarButtonAlignment: "right",
          exportButton: true,
          exportFileName: "admin-queue-trips",
          headerStyle: {
            whiteSpace: 'nowrap',
            backgroundColor: colors.DARKWHITE + '60',
            fontWeight: "bold",
            fontSize: "14px",
            textTransform: "uppercase"
          }
        }}
                  localization={{
            body: {
              emptyDataSourceMessage: t('admin_queue_table_noTripsFound'),
            },
            toolbar: {
              searchPlaceholder: t('admin_queue_table_searchTrips'),
              exportName: t('admin_queue_table_export'),
            },
            pagination: {
              labelRowsSelect: t('admin_queue_table_rows'),
              labelDisplayedRows: t('admin_queue_table_displayedRows'),
              firstTooltip: t('admin_queue_table_firstPage'),
              previousTooltip: t('admin_queue_table.previousPage'),
              nextTooltip: t('admin_queue_table_nextPage'),
              lastTooltip: t('admin_queue_table_lastPage'),
            },
          }}
      />

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle fontWeight={'bold'}>{t('admin_queue_settings_dialog_title')}</DialogTitle>
        <Divider style={{ margin: "0 0" }} />
        <DialogContent>
          <div className={classes.configSection}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box>
                    <Typography fontWeight="bold">
                      {t('admin_queue_settings_dialog_enableNotifications')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('admin_queue_settings_dialog_enableNotificationsDescription')}
                    </Typography>
                  </Box>
                <Switch
                  checked={adminConfig.automaticNotification}
                  onChange={(e) => setAdminConfig(prev => ({
                    ...prev,
                    automaticNotification: e.target.checked
                  }))}
                  color="primary"
                />
              </Box>
            </Box>
            
            <Box sx={{ mb: 3 }}>
                <Typography fontWeight="bold" gutterBottom>
                  {t('admin_queue_settings_dialog_notificationInterval')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={adminConfig.notificationInterval || 30}
                    onChange={(e) => setAdminConfig(prev => ({
                      ...prev,
                      notificationInterval: parseInt(e.target.value),
                      autoAssignmentTimeout: parseInt(e.target.value)
                    }))}
                    inputProps={{ min: 1, max: 300 }}
                    sx={{ width: 80 }}
                  />
                  <Typography variant="body2">{t('admin_queue_settings_dialog_seconds')}</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {t('admin_queue_settings_dialog_notificationIntervalDescription', { interval: adminConfig.notificationInterval || 30 })}
                </Typography>
              </Box>
            
                          <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography fontWeight="bold">
                      {t('admin_queue_settings_dialog_automaticSearch')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('admin_queue_settings_dialog_automaticSearchDescription')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={adminConfig.automaticSearch}
                    onChange={(e) => setAdminConfig(prev => ({
                      ...prev,
                      automaticSearch: e.target.checked
                    }))}
                    color="primary"
                  />
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography fontWeight="bold">
                      {t('admin_queue_settings_dialog_automaticAssignment')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('admin_queue_settings_dialog_automaticAssignmentDescription')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={adminConfig.automaticAssignment}
                    onChange={(e) => setAdminConfig(prev => ({
                      ...prev,
                      automaticAssignment: e.target.checked
                    }))}
                    color="primary"
                  />
                </Box>
              </Box>
          </div>

          <Divider style={{ margin: "20px 0" }} />

                      <div className={classes.configSection}>
              <Typography fontWeight="bold" gutterBottom>
                {t('admin_queue_settings_dialog_notificationPreview')}
              </Typography>
              
              {/* Editable Notification Title */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('admin_queue_settings_dialog_notificationTitle')}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={adminConfig.notificationTitle || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                      setAdminConfig(prev => ({
                        ...prev,
                        notificationTitle: value
                      }));
                  }}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
              
              {/* Editable Notification Message */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('admin_queue_settings_dialog_notificationMessage')}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={adminConfig.notificationMessage || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                      setAdminConfig(prev => ({
                        ...prev,
                        notificationMessage: value
                      }));
                  }}
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
              
              {/* Notification Preview */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    color: '#f44336', 
                    mt: 0.5,
                    '& .MuiSvgIcon-root': { fontSize: 20 }
                  }}>
                    <LocationIcon />
                  </Box>
                  <Box>
                    <Typography variant="body1" fontWeight="bold" color="textPrimary">
                      {adminConfig.notificationTitle || t('admin_queue_settings_dialog_newOrderAvailable')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {adminConfig.notificationMessage || t('admin_queue_settings_dialog_newOrderAvailableDescription')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </div>
        </DialogContent>
        <Divider style={{ margin: "0 0" }} />
        <DialogActions>
          <Button onClick={handleSettingsClose} color="secondary">
            {t('admin_queue_settings_dialog_cancel')}
          </Button>
          <Button onClick={handleConfigUpdate} variant="contained" color="primary">
            {t('admin_queue_settings_dialog_saveConfiguration')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver Assignment Dialog */}
      <Dialog
        open={driverModalOpen}
        onClose={() => setDriverModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              {t('admin_queue_driver_assignment_dialog_selectDriver')}
            </Typography>
            <IconButton
              onClick={() => setDriverModalOpen(false)}
              size="small"
            >
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider style={{ margin: "0 0" }} />
        <DialogContent>
          {selectedBooking && (
            <Box mb={3}>
              <Typography variant="body2">
                {t('admin_queue_driver_assignment_dialog_orderId')}: {selectedBooking.id}
              </Typography>
            </Box>
          )}
          
          {availableDrivers.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {/* Table Header */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                gap: 2,
                p: 2,
                backgroundColor: colors.DARKWHITE + '60',
                mb: 2,
                fontWeight: 'bold',
                fontSize: '14px',
                textTransform: 'uppercase',
              }}>
                <Box>{t('admin_queue_driver_assignment_dialog_driverName')}</Box>
                <Box>{t('admin_queue_driver_assignment_dialog_distance')}</Box>
                <Box>{t('admin_queue_driver_assignment_dialog_availability')}</Box>
                <Box>{t('admin_queue_driver_assignment_dialog_action')}</Box>
              </Box>

              {/* Driver Rows */}
              {availableDrivers.map((driver) => {
                // Determine availability based on driver data
                const isAvailable = driver.queue === false && driver.token !== "token_error";
                const isBusy = driver.queue === true;
                const isDisconnected = driver.token === "token_error";
                
                return (
                  <Box key={driver.id} sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '2fr 1fr 1fr 1fr',
                    gap: 2,
                    mb: 1,
                    alignItems: 'center',
                    px: 2
                  }}>
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {driver.name}
                      </Typography>
                                              <Typography variant="caption" color="textSecondary">
                          {driver.vehicle} • {t('admin_queue_driver_assignment_dialog_rating')}: {driver.rating} ⭐
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">
                          {driver.distance.toFixed(1)} {t('admin_queue_driver_assignment_dialog_km')}
                        </Typography>
                      </Box>
                      <Box>
                        <Chip
                          label={
                            isAvailable ? t('admin_queue_driver_assignment_dialog_available') : 
                            isBusy ? t('admin_queue_driver_assignment_dialog_busy') : 
                            isDisconnected ? t('admin_queue_driver_assignment_dialog_disconnected') : t('admin_queue_driver_assignment_dialog_unknown')
                          }
                          size="small"
                          sx={{
                            backgroundColor: 
                              isAvailable ? '#e8f5e8' : 
                              isBusy ? '#fff3cd' : 
                              isDisconnected ? '#f8d7da' : '#f5f5f5',
                            color: 
                              isAvailable ? '#155724' : 
                              isBusy ? '#856404' : 
                              isDisconnected ? '#721c24' : '#6c757d',
                            fontWeight: '500'
                          }}
                        />
                      </Box>
                      <Box>
                        {isAvailable ? (
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={() => handleDriverSelect(driver)}
                            sx={{ 
                              minWidth: 80,
                              textTransform: 'none',
                              fontWeight: '500'
                            }}
                          >
                            {t('admin_queue_driver_assignment_dialog_select')}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled
                            sx={{ 
                              minWidth: 80,
                              textTransform: 'none',
                              fontWeight: '500',
                              color: '#6c757d',
                              borderColor: '#6c757d'
                            }}
                          >
                            {t('admin_queue_driver_assignment_dialog_unavailable')}
                          </Button>
                        )}
                      </Box>
                  </Box>
                );
              })}
            </Box>
                      ) : (
              <Typography variant="body1" color="textSecondary" style={{ textAlign: "center", padding: "20px" }}>
                {t('admin_queue_driver_assignment_dialog_noDriversAvailable')}
              </Typography>
            )}
        </DialogContent>
        <Divider style={{ margin: "0 0" }} />
        <DialogActions>
          <Button onClick={() => setDriverModalOpen(false)} color="secondary">
            {t('admin_queue_driver_assignment_dialog_cancel')}
          </Button>
          {selectedDriver && (
            <Button
              onClick={handleDriverAssignment}
              variant="contained"
              color="primary"
              disabled={assigning}
            >
              {assigning ? t('admin_queue_driver_assignment_dialog_assigning') : `${t('admin_queue_driver_assignment_dialog_assign')} ${selectedDriver.name}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Trip Confirmation Modal */}
      <Dialog
        open={cancelModalOpen}
        onClose={closeCancelModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: '#f44336' }} />
            <Typography variant="h6" fontWeight="bold">
              {t('admin_queue_cancel_trip_modal_title')}
            </Typography>
          </Box>
        </DialogTitle>
        <Divider style={{ margin: "0 0" }} />
        <DialogContent>
          {tripToCancel && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {t('admin_queue_cancel_trip_modal_message')}
              </Typography>
              
              {/* Trip Information */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  mt: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa'
                }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, overflow: 'hidden' }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {t('admin_queue_cancel_trip_modal_tripId')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {tripToCancel.id}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {t('admin_queue_cancel_trip_modal_customer')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {tripToCancel.customer_name || t('admin_queue_cancel_trip_modal_unknown')}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {t('admin_queue_cancel_trip_modal_from')}
                    </Typography>
                    <Typography variant="body2" noWrap >
                      {tripToCancel.pickup?.add || t('admin_queue_cancel_trip_modal_unknown')}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {t('admin_queue_cancel_trip_modal_to')}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {tripToCancel.drop?.add || t('admin_queue_cancel_trip_modal_unknown')}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              
              <Typography variant="body2" color="error" sx={{ mt: 2, fontStyle: 'italic' }}>
                {t('admin_queue_cancel_trip_modal_warning')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider style={{ margin: "0 0" }} />
        <DialogActions>
          <Button onClick={closeCancelModal} color="secondary" variant="outlined">
            {t('admin_queue_cancel_trip_modal_cancel')}
          </Button>
          <Button 
            onClick={confirmCancelTrip} 
            variant="contained" 
            color="error"
            startIcon={<CancelIcon />}
          >
            {t('admin_queue_cancel_trip_modal_confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminQueue;
